import React, { useState, useContext } from 'react';
import { api as axios } from '../api/api';
import { SnackbarContext } from '../App';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
    SheetClose,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
    Building2, Globe, Linkedin, MapPin, Briefcase, Users, Loader2 
} from "lucide-react";

export default function CompanyCreateModal({ isOpen, onClose, onSuccess }) {
    const showSnackbar = useContext(SnackbarContext);
    const [loading, setLoading] = useState(false);
    
    const [form, setForm] = useState({
        name: '',
        domain: '',
        website: '',
        linkedinUrl: '',
        industry: '',
        employeeSize: '',
        location: '',
        notes: ''
    });

    // Reset form when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setForm({
                name: '',
                domain: '',
                website: '',
                linkedinUrl: '',
                industry: '',
                employeeSize: '',
                location: '',
                notes: ''
            });
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.name.trim()) {
            showSnackbar('Company name is required', 'error');
            return;
        }

        setLoading(true);
        try {
            await axios.post('/api/crm/companies', form);
            showSnackbar('Company created successfully', 'success');
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating company:', error);
            showSnackbar(error.response?.data?.message || 'Error creating company', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent side="right" size="lg" className="p-0">
                <form onSubmit={handleSubmit}>
                    <SheetHeader className="p-6 bg-muted/20 border-b border-border/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <SheetTitle className="text-lg font-bold tracking-tight">Create Company</SheetTitle>
                                <SheetDescription className="text-xs font-medium">
                                    Add a new organization to your workspace CRM.
                                </SheetDescription>
                            </div>
                        </div>
                    </SheetHeader>

                    <div className="p-6 grid grid-cols-2 gap-4">
                        {/* Identity Section */}
                        <div className="space-y-4 col-span-2">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="h-px flex-1 bg-border/50"></div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 px-2 font-mono">Company Profile</span>
                                <div className="h-px flex-1 bg-border/50"></div>
                            </div>
                            <div className="space-y-1.5 col-span-2">
                                <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                    <Building2 className="w-3 h-3 opacity-60" /> Company Name <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    required
                                    placeholder="Acme Corp"
                                    value={form.name}
                                    onChange={e => setForm({...form, name: e.target.value})}
                                    className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50 font-bold"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Globe className="w-3 h-3 opacity-60" /> Domain
                                    </Label>
                                    <Input
                                        placeholder="acme.com"
                                        value={form.domain}
                                        onChange={e => setForm({...form, domain: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Globe className="w-3 h-3 opacity-60" /> Website
                                    </Label>
                                    <Input
                                        placeholder="https://acme.com"
                                        value={form.website}
                                        onChange={e => setForm({...form, website: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Details Section */}
                        <div className="space-y-4 col-span-2 mt-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Briefcase className="w-3 h-3 opacity-60" /> Industry
                                    </Label>
                                    <Input
                                        placeholder="Software"
                                        value={form.industry}
                                        onChange={e => setForm({...form, industry: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Users className="w-3 h-3 opacity-60" /> Employee Size
                                    </Label>
                                    <Input
                                        placeholder="50-200"
                                        value={form.employeeSize}
                                        onChange={e => setForm({...form, employeeSize: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3 opacity-60" /> Location
                                    </Label>
                                    <Input
                                        placeholder="San Francisco, CA"
                                        value={form.location}
                                        onChange={e => setForm({...form, location: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-foreground/80 flex items-center gap-1.5">
                                        <Linkedin className="w-3 h-3 opacity-60" /> LinkedIn
                                    </Label>
                                    <Input
                                        placeholder="https://linkedin.com/company/acmecorp"
                                        value={form.linkedinUrl}
                                        onChange={e => setForm({...form, linkedinUrl: e.target.value})}
                                        className="h-9 text-sm focus-visible:ring-primary border-border bg-card/50"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5 col-span-2">
                                <Label className="text-xs font-bold text-foreground/80">Internal Notes</Label>
                                <Textarea
                                    placeholder="Add any strategic notes about this account..."
                                    value={form.notes}
                                    onChange={e => setForm({...form, notes: e.target.value})}
                                    className="resize-none h-20 text-sm focus-visible:ring-primary border-border bg-card/50"
                                />
                            </div>
                        </div>
                    </div>

                    <SheetFooter className="p-4 bg-muted/20 border-t border-border/50 flex sm:justify-between items-center">
                        <p className="text-[10px] text-muted-foreground/60 italic hidden sm:block">
                            * Account will be deduplicated against domain.
                        </p>
                        <div className="flex gap-2">
                            <SheetClose asChild>
                                <Button type="button" variant="outline" disabled={loading} className="h-9 px-4 text-xs font-bold uppercase tracking-widest border-border">
                                    Cancel
                                </Button>
                            </SheetClose>
                            <Button type="submit" disabled={loading} className="h-9 px-6 text-xs font-bold uppercase tracking-widest bg-primary hover:bg-primary/90 shadow-glow shadow-primary/20">
                                {loading ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Creating...</> : 'Save Account'}
                            </Button>
                        </div>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
}
